import { ActionTypes } from "./odontogram.actions";
import { Action } from "@ngrx/store";
import { Odontogram, Tooth } from "./odontogram.model";
import { ActionTypes as DiagnosisActionTypes } from "../plan-editor/diagnosis/diagnosis.actions";
import { ActionTypes as PlanActionTypes } from "../plan-editor/plan/plan.actions";
import { ActionTypes as PlanEditorActionTypes } from "../plan-editor/plan-editor.actions";

export interface State {
  teeth: Tooth[];
  selectedTeeth: number[];
  q1Selected: boolean;
  q2Selected: boolean;
  q3Selected: boolean;
  q4Selected: boolean;
  selectionMode: string;
}

const odontogram = new Odontogram();

export const initialState: State = {
  teeth: odontogram.teeth,
  selectedTeeth: [],
  q1Selected: false,
  q2Selected: false,
  q3Selected: false,
  q4Selected: false,
  selectionMode: "single",
};

const q1 = [18, 17, 16, 15, 14, 13, 12, 11];
const q2 = [21, 22, 23, 24, 25, 26, 27, 28];
const q3 = [48, 47, 46, 45, 44, 43, 42, 41];
const q4 = [31, 32, 33, 34, 35, 36, 37, 38];

let newSelection;

export function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case ActionTypes.CLEAR_TEETH_SELECTION:
      return Object.assign({}, state, {
        selectedTeeth: [],
        q1Selected: false,
        q2Selected: false,
        q3Selected: false,
        q4Selected: false,
      });

    case ActionTypes.TOGGLE_TOOTH:
      newSelection = toggleTeethSelection(
        state.selectedTeeth,
        [action.payload],
        state.selectedTeeth.indexOf(action.payload) === -1,
        state.selectionMode,
      );
      return Object.assign({}, state, {
        selectedTeeth: newSelection,
      });

    case ActionTypes.TOGGLE_QUADRANT_1:
      newSelection = toggleTeethSelection(
        state.selectedTeeth,
        q1,
        !state.q1Selected,
      );
      return Object.assign({}, state, {
        selectedTeeth: newSelection,
        q1Selected: !state.q1Selected,
      });

    case ActionTypes.TOGGLE_QUADRANT_2:
      newSelection = toggleTeethSelection(
        state.selectedTeeth,
        q2,
        !state.q2Selected,
      );
      return Object.assign({}, state, {
        selectedTeeth: newSelection,
        q2Selected: !state.q2Selected,
      });

    case ActionTypes.TOGGLE_QUADRANT_3:
      newSelection = toggleTeethSelection(
        state.selectedTeeth,
        q3,
        !state.q3Selected,
      );
      return Object.assign({}, state, {
        selectedTeeth: newSelection,
        q3Selected: !state.q3Selected,
      });

    case ActionTypes.TOGGLE_QUADRANT_4:
      newSelection = toggleTeethSelection(
        state.selectedTeeth,
        q4,
        !state.q4Selected,
      );
      return Object.assign({}, state, {
        selectedTeeth: newSelection,
        q4Selected: !state.q4Selected,
      });

    case ActionTypes.RESET_ODONTOGRAM:
    case PlanEditorActionTypes.CHANGE_STEP: // TODO: check if change step should be replace also by the reset action
      return Object.assign({}, state, initialState);

    case DiagnosisActionTypes.SELECT_ITEM:
    case PlanActionTypes.SELECT_ITEM:
      return Object.assign({}, state, {
        selectionMode: action.payload.selectionMode,
      });

    default:
      return state;
  }
}

function toggleTeethSelection(
  selectedTeeth,
  newTeethNumbers,
  add,
  selectionMode = "single",
) {
  let selected = selectedTeeth.slice(0);

  if (selectionMode === "single") {
    if (add) {
      newTeethNumbers.forEach((number) => {
        if (selected.indexOf(number) === -1) {
          selected.push(number);
        }
      });
    } else {
      newTeethNumbers.forEach((number) => {
        const idx = selected.indexOf(number);
        if (idx > -1) {
          selected.splice(idx, 1);
        }
      });
    }
  } else {
    if (newTeethNumbers.length > 1) {
      console.error(
        "In range selection mode the selected cannot be more than 1 at a time",
      );
      return selected;
    }

    if (selected.length > 1 || selected.length === 0) {
      // restart range selection
      selected = [newTeethNumbers[0]];
    } else if (selected.length === 1) {
      // we have the start teeth, compute the teeth to be selected
      selected = getTeethRange(selected[0], newTeethNumbers[0]);
    }
  }
  selected.sort();
  return selected;
}

function sameJaw(tooth1, tooth2) {
  return (
    (Math.floor(tooth1 / 10) <= 2 ? "upper" : "lower") ===
    (Math.floor(tooth2 / 10) <= 2 ? "upper" : "lower")
  );
}

function getTeethRange(tooth1, tooth2) {
  if (!sameJaw(tooth1, tooth2)) {
    console.error("Teeth are not from the same jaw");
    return [];
  }

  const _q1 = Math.floor(tooth1 / 10);
  const _q2 = Math.floor(tooth2 / 10);

  // compute range

  if (_q1 === _q2) {
    // same quadrant
    return tooth1 < tooth2 ? range(tooth1, tooth2) : range(tooth2, tooth1);
  } else {
    // different quadrant
    return [...range(_q1 * 10 + 1, tooth1), ...range(_q2 * 10 + 1, tooth2)];
  }
}

function range(start, end) {
  return Array.from({ length: end - start + 1 }, (v, k) => k + start);
}

/*
export const getTeeth = (state: State) => state.teeth.map(tooth => {
    tooth.selected = state.selectedTeeth.indexOf(tooth.number) > -1;
    return tooth;
});
*/
export const getQ1Selected = (state: State) => state.q1Selected;
export const getQ2Selected = (state: State) => state.q2Selected;
export const getQ3Selected = (state: State) => state.q3Selected;
export const getQ4Selected = (state: State) => state.q4Selected;
export const getSelectedTeeth = (state: State) => state.selectedTeeth;



// WEBPACK FOOTER //
// /app/src/app/core/store/odontogram/odontogram.reducer.ts